import { Component, Input, Output, EventEmitter, OnInit, AfterContentInit } from '@angular/core';
import { PlycaDataService } from '../../../services/plyca.service';
import {
  EnvelopeDTO,
  ProcurementProjectLotDTO,
  ProcurementProjectLotGroupDTO,
  ValidationResultDTO,
  ResultTypeDTO,
  ValidationExecutionDTO
} from '../../api/plyca-dtos-v2';
import { SelectItem } from 'primeng-lts/api';
import { Configuration } from '../../conf/app.constants';
import { TenderingBaseComponent } from './tenderingBase.component';
import { forkJoin, of, from } from 'rxjs';
import {
  TenderingPhaseDTO,
  ProcurementCommitteeDTO,
  TenderingPhaseState
} from '../../api/plyca-dtos';
import { catchError } from 'rxjs/operators';
import { HelpType } from '../../components/common/help/help.service';

@Component({
  selector: 'tenderingEnvelopes',
  templateUrl: 'tenderingEnvelopes.template.html'
})
export class TenderingEnvelopesComponent
  extends TenderingBaseComponent<EnvelopeDTO>
  implements AfterContentInit {
  public projectLots: Array<SelectItem>;
  public projectHaveLots: boolean;

  @Input()
  private tenderingPhases: Array<TenderingPhaseDTO>;
  @Output()
  orderModified = new EventEmitter<EnvelopeDTO[]>();

  constructor(
    private _plycaService: PlycaDataService,
    private configuration: Configuration
  ) {
    super();
    this.colHeaders = [
      'General.name',
      'Envelope.type',
      'Envelope.lots',
      'General.state',
      'General.actions'
    ];
    this.validations = new Map();
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
    let projectUrl =
      this.configuration.PROCUREMENTPROJ_SERVICE_URL + this.getDossierId();
    const initObs = forkJoin(
      this._plycaService.query<ProcurementProjectLotDTO>(
        projectUrl + this.configuration.PROCUREMENTPROJLOT_SERVICE_ACTION
      ),
      this._plycaService.query<ProcurementProjectLotGroupDTO>(
        projectUrl + this.configuration.PROCUREMENTPROJLOTGRP_SERVICE_ACTION
      )
    );
    initObs.subscribe(
      (result: any[]) => {
        let hasLots = result[0] && result[0].content;
        let hasLotGroups = result[1] && result[1].content;
        this.projectLots = new Array<SelectItem>();
        if (hasLots) {
          result[0].content
            .map((lot: ProcurementProjectLotDTO) => {
              return {
                label: lot.lotNumber + '-' + lot.description,
                value: lot
              } as SelectItem;
            })
            .forEach((item: SelectItem) => this.projectLots.push(item));
        }
        if (hasLotGroups) {
          result[1].content
            .map((lot: ProcurementProjectLotGroupDTO) => {
              return {
                label: lot.groupId + '-' + lot.name,
                value: lot
              } as SelectItem;
            })
            .forEach((item: SelectItem) => this.projectLots.push(item));
        }
        this.projectHaveLots = hasLotGroups || hasLots;
      },
      (error: any) => this.showServerError(error),
      () => this.setLoaded(true)
    );
  }

  getFormSettingURL(): string {
    return 'envelopeISettings.json';
  }

  getDialogTitle(): string {
    return 'Envelope.edit.title';
  }

  public getLots(envelope: EnvelopeDTO): string {
    let lots = new Array<any>();
    if (envelope.lots) {
      envelope.lots
        .map((lot: ProcurementProjectLotDTO) => lot.lotNumber)
        .forEach((lotNumber: number) => lots.push(lotNumber));
    }
    lots = lots.sort();
    let lotGroups = new Array<any>();
    if (envelope.lotGroups) {
      envelope.lotGroups
        .map((lot: ProcurementProjectLotGroupDTO) => lot.name)
        .forEach((lotGroupName: string) => lotGroups.push(lotGroupName));
    }
    lotGroups = lotGroups.sort();
    return lots.concat(lotGroups).join(', ');
  }

  public getStateDescriptionEnvelope(envelope: EnvelopeDTO): string {
    let phase = this.getPhaseAssignedToEnvelope(envelope);
    let desc: string;
    if (this.isEnvelopeAssignedToPublishedPhase(phase)) {
      desc = 'Envelope.state.publish';
    } else {
      desc = 'Envelope.state.pending';
    }
    return this.translationService.instant(desc);
  }

  private isEnvelopeAssignedToPublishedPhase(
    phase: TenderingPhaseDTO
  ): boolean {
    return phase &&
      phase.state === TenderingPhaseState.PUBLISHED;
  }

  public assignedToPhase(envelope: EnvelopeDTO): boolean {
    return this.getPhaseAssignedToEnvelope(envelope) !== undefined;
  }

  private getPhaseAssignedToEnvelope(envelope: EnvelopeDTO): TenderingPhaseDTO {
    return this.tenderingPhases
      ? this.tenderingPhases.find(
        (phase: TenderingPhaseDTO) =>
          phase.envelopes &&
          phase.envelopes.find(
            (phaseEnvelope: EnvelopeDTO) =>
              phaseEnvelope.identifier === envelope.identifier
          ) !== undefined
      )
      : undefined;
  }

  getTagsIdentifier(): string[] {
    return [HelpType.EDIT_SOBRE_ELEC];
  }

  getServiceURL(): string {
    return this.configuration.ENVELOPE_SERVICE_URL;
  }

  /**
   * Sobreescrito para indicar orden del nuevo sobre
   */
  public newData(): void {
    let newEnvelope = new EnvelopeDTO();
    newEnvelope.order = (this.data) ? this.data.length + 1 : 1;
    super.editData(newEnvelope);
  }

  dtoToBean(dto: EnvelopeDTO) {
    let bean: any;
    bean = dto;
    // lotes asociados al sobre
    bean.selectedLots = new Array<
      ProcurementProjectLotDTO | ProcurementProjectLotGroupDTO
    >();
    let lots = dto.lots;
    if (lots && lots.length > 0) {
      lots.forEach((lot: ProcurementProjectLotDTO) =>
        bean.selectedLots.push(lot)
      );
    }
    let lotGroups = dto.lotGroups;
    if (lotGroups && lotGroups.length > 0) {
      lotGroups.forEach((lotGroup: ProcurementProjectLotGroupDTO) =>
        bean.selectedLots.push(lotGroup)
      );
    }
    // lotes seleccionables
    bean.projectLots = this.projectLots;
    return bean;
    // return new EnvelopeBean(dto, this.projectLots);
  }

  beanToDTO(bean: any): EnvelopeDTO {
    let dto: any;
    dto = bean;
    // lotes y agrupacion de lotes
    dto.lots = new Array<ProcurementProjectLotDTO>();
    dto.lotGroups = new Array<ProcurementProjectLotGroupDTO>();
    if (bean.selectedLots) {
      bean.selectedLots.forEach(
        (
          selection: ProcurementProjectLotDTO | ProcurementProjectLotGroupDTO
        ) => {
          if ('lotId' in selection) {
            dto.lots.push(selection);
          } else {
            dto.lotGroups.push(selection);
          }
        }
      );
    }
    // eliminar propiedades presentacion
    delete dto.projectLots;
    delete dto.selectedLots;
    if (dto.files) {
      dto.files.forEach((file: any) => {
        delete file.parent;
        delete file._parent;
      });
    }
    return dto as EnvelopeDTO;
  }

  public canDelete(envelope: EnvelopeDTO): boolean {
    let inPhase = false;
    if (this.tenderingPhases) {
      inPhase =
        this.tenderingPhases.find(
          (phase: TenderingPhaseDTO) =>
            phase.envelopes &&
            phase.envelopes.find(
              (phaseEnvelope: EnvelopeDTO) =>
                phaseEnvelope.identifier === envelope.identifier
            ) !== undefined
        ) !== undefined;
    }
    return !inPhase;
  }

  public orderEnvelopes(event: {
    dragIndex: number,
    dropIndex: number
  }): void {
    // indices drag and drop 1-based
    let dragIndex = event.dragIndex + 1;
    let dropIndex = event.dropIndex + 1;
    let minIndex = Math.min(dragIndex, dropIndex);
    let maxIndex = Math.max(dragIndex, dropIndex);

    let envelopes2Update = new Array<EnvelopeDTO>();
    this.data.forEach((envelope: EnvelopeDTO) => {
      if (!envelope.order) {
        // si no tiene norden establecido se pone al final
        envelope.order = Number.MAX_VALUE;
        envelopes2Update.push(envelope);
      } else if (envelope.order >= minIndex && envelope.order <= maxIndex) {
        // solo cambian los sobres cuyo orden esta entre las posiciones modificadas
        if (envelope.order === dragIndex) {
          // cambiar orden del elemento seleccionado
          envelope.order = dropIndex;
          envelopes2Update.push(envelope);
        } else if (dragIndex > dropIndex) {
          // el sobre se sube de orden (menor numero) -> aumentar en uno el orden del resto en el intervalo de cambio
          envelope.order++;
          envelopes2Update.push(envelope);
        } else {
          // el sobre se baja de orden -> disminuir en uno el orden del resto en el intervalo de cambio
          envelope.order--;
          envelopes2Update.push(envelope);
        }
      }
    });
    if (envelopes2Update.length > 0) {
      this.setLoaded(false);
      this.subscribe(from(envelopes2Update),
        (envelope: EnvelopeDTO) => {
          let auxEnvelope = new EnvelopeDTO();
          auxEnvelope.identifier = envelope.identifier;
          auxEnvelope.order = envelope.order;
          this.subscribe(this.plycaService
            .patch<EnvelopeDTO>(this.getServiceURL(), auxEnvelope)
            .pipe(
              catchError((e: any) => {
                console.error('Error al actualizar el sobre ', envelope);
                return of(e);
              })
            ));
        },
        () => undefined,
        () => {
          this.setLoaded(true);
          this.orderModified.next(this.data);
        }
      );
    }
  }

  public showNotAssignetToPhase(): void {
    let validation = new ValidationResultDTO();
    validation.result = ResultTypeDTO.WARNING;
    validation.errors = [];
    let error = new ValidationExecutionDTO();
    error.message = this.translationService.instant('Envelope.warn.phaseAssociation');
    validation.errors.push(error);
    this.showValidationResult(validation);
  }
}
